/**
 * Owner: Masa Bayu
 * File: identity.ts
 * Date:Time: 2026-09-15 19:18
 * Description: QXRead T0 identity, session, and role foundation (≠ entitlement).
 */
import { createQxreadId } from './id';
import { assertOptionalTrimmed, assertTimestamp, assertValidId } from './guards';
import { assertNoForbiddenFoundationKey } from './authority';
export const QXREAD_ROLES = ['guest', 'reader', 'author', 'admin'];
export const QXREAD_SESSION_STATES = [
    'anonymous',
    'authenticating',
    'authenticated',
    'expired',
    'revoked',
];
function assertRole(value, label) {
    if (typeof value !== 'string' || !QXREAD_ROLES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_ROLES.join(', ')}`);
    }
    return value;
}
function assertSessionState(value, label) {
    if (typeof value !== 'string' || !QXREAD_SESSION_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_SESSION_STATES.join(', ')}`);
    }
    return value;
}
/**
 * Create an immutable account record.
 * Roles are grants for chrome/capability routing — not publication access authority.
 */
export function createQxreadAccount(options = {}) {
    assertNoForbiddenFoundationKey(options, 'QXRead account');
    const accountId = options.accountId !== undefined ? options.accountId : createQxreadId('qxrdacc');
    assertValidId(accountId, 'QXRead accountId');
    const createdAt = options.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead account createdAt');
    const roles = Object.freeze((options.roles ?? ['reader']).map((role) => assertRole(role, 'QXRead account role')));
    if (roles.length === 0) {
        throw new Error('QXRead account roles must not be empty');
    }
    return Object.freeze({
        accountId,
        displayName: assertOptionalTrimmed(options.displayName, 'QXRead account displayName'),
        roles,
        createdAt,
    });
}
/**
 * Create a session. Session authenticity ≠ entitlement to read publications.
 */
export function createQxreadSession(options = {}) {
    assertNoForbiddenFoundationKey(options, 'QXRead session');
    const state = assertSessionState(options.state ?? 'anonymous', 'QXRead session state');
    const sessionId = options.sessionId !== undefined ? options.sessionId : createQxreadId('qxrdsess');
    assertValidId(sessionId, 'QXRead sessionId');
    const issuedAt = options.issuedAt ?? new Date().toISOString();
    assertTimestamp(issuedAt, 'QXRead session issuedAt');
    let accountId = null;
    if (options.accountId !== undefined && options.accountId !== null) {
        assertValidId(options.accountId, 'QXRead session accountId');
        accountId = options.accountId;
    }
    if (state === 'authenticated' && accountId === null) {
        throw new Error('QXRead authenticated session requires accountId');
    }
    if ((state === 'anonymous' || state === 'authenticating') && accountId !== null) {
        throw new Error(`QXRead session state "${state}" must not bind accountId`);
    }
    let expiresAt = null;
    if (options.expiresAt !== undefined && options.expiresAt !== null) {
        assertTimestamp(options.expiresAt, 'QXRead session expiresAt');
        expiresAt = options.expiresAt;
    }
    return Object.freeze({
        sessionId,
        accountId,
        state,
        issuedAt,
        expiresAt,
    });
}
/** UI chrome mode is derived — never security authority by itself. */
export function deriveChromeMode(roles) {
    if (roles.includes('admin')) {
        return 'admin';
    }
    if (roles.includes('author')) {
        return 'author';
    }
    if (roles.includes('reader')) {
        return 'reader';
    }
    return 'guest';
}
export function isSessionAuthenticated(session) {
    return session.state === 'authenticated' && session.accountId !== null;
}
//# sourceMappingURL=identity.js.map